import {gql, useApolloClient, useMutation, useQuery} from '@apollo/client';
import {showToast} from 'common';
import {getDomainGraphqlError} from 'graphqlsupport';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useCurrentUser, useToken} from 'security';
import {useApplicationIsInternalAuth, useApplicationIsProviderCPSupport} from 'common/component/ApplicationRootContext';
import {useOfferByGuid} from '../../hook/useOfferByGuidNoDupCheck';
import {useOfferConversationSummary, useSharedMessaging} from '../../hook/useSharedMessaging';
import {useSubmittersByGuids} from '../../hook/useSubmittersByGuids';
import {appendMessage, cacheSenders, countUnreadMessages, findUnreadDividerIndex, getLastReadMessageGuidAfterMarkAsUnread, getLatestUrgentState, getSideLastReadAt, isCurrentSideReceipt, mergeMessages, messagesFromEdges} from './messageUtils';
import type {Message, MessagesConnection, MessageSender, OfferConversationPopupProps, OfferConversationState, OfferConversation} from './types';

const MESSAGE_PAGE_SIZE = 30;
const TOP_LOAD_THRESHOLD = 80;

const OFFER_CONVERSATION_QUERY = gql`
    query OfferConversation($referralGuid: String!, $providerGuid: String!, $first: Int, $after: String) {
        conversationByReferralAndProvider(referralGuid: $referralGuid, providerGuid: $providerGuid) {
            guid
            referralGuid
            providerGuid
            offerGuid
            urgent
            createdAt
            mySideLastReadAt
        }
        messagesByReferralAndProvider(referralGuid: $referralGuid, providerGuid: $providerGuid, first: $first, after: $after) {
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
            }
            edges {
                cursor
                node {
                    id
                    guid
                    conversationGuid
                    senderGuid
                    internal
                    messageType
                    content
                    createdAt
                    sender {
                        guid
                        firstName
                        lastName
                        email
                    }
                }
            }
        }
    }
`;

const OFFER_MESSAGES_QUERY = gql`
    query OfferMessages($conversationGuid: String!, $first: Int, $after: String) {
        messagesByConversation(conversationGuid: $conversationGuid, first: $first, after: $after) {
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
            }
            edges {
                cursor
                node {
                    id
                    guid
                    conversationGuid
                    senderGuid
                    internal
                    messageType
                    content
                    createdAt
                    sender {
                        guid
                        firstName
                        lastName
                        email
                    }
                }
            }
        }
    }
`;

const SEND_OFFER_MESSAGE_MUTATION = gql`
    mutation SendMessageByReferralAndProvider($referralGuid: String!, $providerGuid: String!, $activeOfferGuid: String!, $content: String!) {
        sendMessageByReferralAndProvider(referralGuid: $referralGuid, providerGuid: $providerGuid, activeOfferGuid: $activeOfferGuid, content: $content) {
            guid
            conversationGuid
            senderGuid
            internal
            messageType
            content
            createdAt
            sender {
                guid
                firstName
                lastName
                email
            }
        }
    }
`;

const MARK_OFFER_MESSAGES_AS_READ_MUTATION = gql`
    mutation MarkOfferMessagesAsRead($conversationGuid: String!, $upToMessageGuid: String!) {
        markOfferMessagesAsRead(conversationGuid: $conversationGuid, upToMessageGuid: $upToMessageGuid)
    }
`;

const MARK_OFFER_MESSAGES_AS_UNREAD_MUTATION = gql`
    mutation MarkOfferMessagesAsUnread($conversationGuid: String!) {
        markOfferMessagesAsUnread(conversationGuid: $conversationGuid)
    }
`;

const TOGGLE_URGENT_FLAG_MUTATION = gql`
    mutation ToggleUrgentFlagByReferralAndProvider($referralGuid: String!, $providerGuid: String!, $activeOfferGuid: String!, $urgent: Boolean!) {
        toggleUrgentFlagByReferralAndProvider(referralGuid: $referralGuid, providerGuid: $providerGuid, activeOfferGuid: $activeOfferGuid, urgent: $urgent) {
            guid
            referralGuid
            providerGuid
            offerGuid
            urgent
            createdAt
        }
    }
`;

type OfferConversationQueryResult = {
    conversationByReferralAndProvider: OfferConversation | null;
    messagesByReferralAndProvider: MessagesConnection;
};

type OfferMessagesQueryResult = {
    messagesByConversation: MessagesConnection;
};

function useIsMounted() {
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);
    return isMountedRef;
}

function useReportConversationState(state: OfferConversationState, onStateChange?: (state: OfferConversationState) => void) {
    useEffect(() => {
        onStateChange?.(state);
    }, [onStateChange, state]);
}

function useOfferConversationData(
    data: OfferConversationQueryResult | undefined,
    setConversationGuid: React.Dispatch<React.SetStateAction<string | null>>,
    setUrgent: React.Dispatch<React.SetStateAction<boolean>>,
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
    setPageInfo: React.Dispatch<React.SetStateAction<MessagesConnection['pageInfo'] | null>>,
    senderCacheRef: React.MutableRefObject<Map<string, MessageSender>>,
    shouldScrollToBottomRef: React.MutableRefObject<boolean>,
    setInitialLastReadAt: React.Dispatch<React.SetStateAction<Date | null | undefined>>
) {
    useEffect(() => {
        if (!data) return;
        if (data.conversationByReferralAndProvider) {
            setConversationGuid(data.conversationByReferralAndProvider.guid);
            setUrgent(data.conversationByReferralAndProvider.urgent);
            // undefined = field absent (older backend), null = side never read, Date = cursor
            const mySideLastReadAt = data.conversationByReferralAndProvider.mySideLastReadAt;
            setInitialLastReadAt(mySideLastReadAt === undefined ? undefined : mySideLastReadAt ? new Date(mySideLastReadAt) : null);
        }
        if (data.messagesByReferralAndProvider) {
            const initialMessages = messagesFromEdges(data.messagesByReferralAndProvider.edges);
            cacheSenders(initialMessages, senderCacheRef.current);
            setMessages((current) => mergeMessages(current as Message[], initialMessages));
            setPageInfo(data.messagesByReferralAndProvider.pageInfo);
            shouldScrollToBottomRef.current = true;
        }
    }, [data, senderCacheRef, setConversationGuid, setInitialLastReadAt, setMessages, setPageInfo, setUrgent, shouldScrollToBottomRef]);
}

function useConversationScroll(
    isOpen: boolean,
    isLoadingOlder: boolean,
    messages: Message[],
    messagesContainerRef: React.RefObject<HTMLDivElement>,
    pendingScrollRestoreRef: React.MutableRefObject<{height: number; top: number} | null>,
    shouldScrollToBottomRef: React.MutableRefObject<boolean>,
    isNearBottomRef: React.MutableRefObject<boolean>,
    wasOpenRef: React.MutableRefObject<boolean>
) {
    useLayoutEffect(() => {
        const wasOpen = wasOpenRef.current;
        wasOpenRef.current = isOpen;
        const container = messagesContainerRef.current;
        const pendingRestore = pendingScrollRestoreRef.current;
        if (!container) return;
        if (pendingRestore && !isLoadingOlder) {
            container.scrollTop = pendingRestore.top + (container.scrollHeight - pendingRestore.height);
            pendingScrollRestoreRef.current = null;
            return;
        }
        if (isOpen && (!wasOpen || shouldScrollToBottomRef.current || isNearBottomRef.current) && messages.length > 0) {
            container.scrollTop = container.scrollHeight;
            shouldScrollToBottomRef.current = false;
        }
    }, [isLoadingOlder, isOpen, messages, messagesContainerRef, pendingScrollRestoreRef, shouldScrollToBottomRef, isNearBottomRef, wasOpenRef]);
}

function useLatestUrgentFromMessages(messages: Message[], setUrgent: React.Dispatch<React.SetStateAction<boolean>>) {
    useEffect(() => {
        const latestUrgentState = getLatestUrgentState(messages);
        if (latestUrgentState !== null) setUrgent(latestUrgentState);
    }, [messages, setUrgent]);
}

function useHydrateMissingSenders(
    conversationGuid: string | null,
    currentUserGuid: string | undefined,
    messages: Message[],
    senderCacheRef: React.MutableRefObject<Map<string, MessageSender>>,
    senderHydrationRequestedGuidsRef: React.MutableRefObject<Set<string>>,
    loadSubmitters: ReturnType<typeof useSubmittersByGuids>,
    bumpSenderCache: () => void
) {
    useEffect(() => {
        const missingGuids = new Set<string>();
        messages.forEach((message) => {
            if (message.messageType === 'TEXT' && message.senderGuid !== currentUserGuid && !message.sender && !senderCacheRef.current.has(message.senderGuid) && !senderHydrationRequestedGuidsRef.current.has(message.senderGuid)) {
                missingGuids.add(message.senderGuid);
            }
        });
        if (!conversationGuid || missingGuids.size === 0) return;
        missingGuids.forEach((guid) => senderHydrationRequestedGuidsRef.current.add(guid));
        loadSubmitters(Array.from(missingGuids)).then((result) => {
            if (!result.data?.submittersByGuids) return;
            result.data.submittersByGuids.forEach((submitter) => senderCacheRef.current.set(submitter.guid, {guid: submitter.guid, firstName: submitter.firstName, lastName: submitter.lastName, email: submitter.email}));
            bumpSenderCache();
        }).catch((error: unknown) => console.error('Failed to load message sender details', error));
    }, [bumpSenderCache, conversationGuid, currentUserGuid, loadSubmitters, messages, senderCacheRef, senderHydrationRequestedGuidsRef]);
}

type UseOfferConversationResult = {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    input: string;
    setInput: React.Dispatch<React.SetStateAction<string>>;
    urgent: boolean;
    hasConversation: boolean;
    unreadCount: number;
    messages: Message[];
    isMessagesLoading: boolean;
    isLoadingOlder: boolean;
    isSendingMessage: boolean;
    isMarkingMessages: boolean;
    isReferralProviderReady: boolean;
    offerData: ReturnType<typeof useOfferByGuid>['data'];
    offerLoading: boolean;
    currentUser: ReturnType<typeof useCurrentUser>;
    isInternalAuth: boolean;
    isReadOnly: boolean;
    isMessagingConnected: boolean;
    senderCache: Map<string, MessageSender>;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    messagesContainerRef: React.RefObject<HTMLDivElement>;
    handleMessageScroll: React.UIEventHandler<HTMLDivElement>;
    handleSend: () => Promise<void>;
    handleToggleUrgent: () => Promise<void>;
    handleMarkAsRead: () => Promise<void>;
    handleMarkAsUnread: () => Promise<void>;
    canToggleRead: boolean;
    canMarkAsUnread: boolean;
    unreadDividerIndex: number;
};

export function useOfferConversation({
    offerGuid,
    conversationSummary,
    readOnly: readOnlyProp = false,
    open: openProp,
    onClose,
    onStateChange,
}: OfferConversationPopupProps): UseOfferConversationResult {
    const currentUser = useCurrentUser();
    const token = useToken();
    const isInternalAuth = useApplicationIsInternalAuth();
    const isProviderCPSupport = useApplicationIsProviderCPSupport();
    const isReadOnly = readOnlyProp || isProviderCPSupport;

    const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
    const isOpen = openProp ?? uncontrolledIsOpen;
    const [input, setInput] = useState('');
    const [urgent, setUrgent] = useState(Boolean(conversationSummary?.urgent));
    const [hasConversation, setHasConversation] = useState(Boolean(conversationSummary?.hasConversation));
    const [conversationGuid, setConversationGuid] = useState<string | null>(null);
    const [pageInfo, setPageInfo] = useState<MessagesConnection['pageInfo'] | null>(null);
    const [initialLastReadAt, setInitialLastReadAt] = useState<Date | null | undefined>(undefined);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const [, setSenderCacheVersion] = useState(0);
    const bumpSenderCache = useCallback(() => setSenderCacheVersion((version) => version + 1), []);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const senderCacheRef = useRef(new Map<string, MessageSender>());
    const senderHydrationRequestedGuidsRef = useRef(new Set<string>());
    const pendingScrollRestoreRef = useRef<{height: number; top: number} | null>(null);
    const shouldScrollToBottomRef = useRef(true);
    const isNearBottomRef = useRef(true);
    const wasOpenRef = useRef(false);
    const isLoadingOlderRef = useRef(false);
    const autoReadAttemptedRef = useRef(false);

    const isMountedRef = useIsMounted();
    const {data: offerData, loading: offerLoading} = useOfferByGuid(offerGuid, !isOpen);
    const referralGuid = offerData?.offerByGuid?.referral?.guid;
    const providerGuid = offerData?.offerByGuid?.provider?.guid;
    const isReferralProviderReady = Boolean(referralGuid && providerGuid);
    const activeConversationGuid = isOpen ? conversationGuid : null;
    const {connected: isMessagingConnected, messages: rawMessages, setMessages, readReceipts, setReadReceipts} = useSharedMessaging(activeConversationGuid ? token : null, activeConversationGuid);
    const wasMessagingConnectedRef = useRef(isMessagingConnected);
    const liveSummary = useOfferConversationSummary(offerGuid, token);
    const messages = rawMessages as Message[];
    const {data, loading: isConversationQueryLoading} = useQuery<OfferConversationQueryResult>(OFFER_CONVERSATION_QUERY, {
        variables: {referralGuid, providerGuid, first: MESSAGE_PAGE_SIZE, after: '0'},
        skip: !isOpen || !hasConversation || !isReferralProviderReady,
        fetchPolicy: 'no-cache',
    });
    // While the offer (and therefore referralGuid/providerGuid) is still loading, the
    // conversation query above is skipped, so account for that so the message list keeps
    // showing a loading state instead of briefly flashing "no messages yet".
    const isMessagesLoading = isConversationQueryLoading || (hasConversation && !isReferralProviderReady);
    const client = useApolloClient();
    const [sendOfferMessage, {loading: isSendingMessage}] = useMutation(SEND_OFFER_MESSAGE_MUTATION);
    const [markOfferMessagesAsRead, {loading: isMarkingAsRead}] = useMutation(MARK_OFFER_MESSAGES_AS_READ_MUTATION);
    const [markOfferMessagesAsUnread, {loading: isMarkingAsUnread}] = useMutation(MARK_OFFER_MESSAGES_AS_UNREAD_MUTATION);
    const [toggleUrgentFlag] = useMutation(TOGGLE_URGENT_FLAG_MUTATION);
    const loadSubmitters = useSubmittersByGuids();

    const isMarkingMessages = isMarkingAsRead || isMarkingAsUnread;

    // Read state is side-based: a conversation is unread for the current user when there are
    // TEXT messages from the other side created after the current side's latest read receipt.
    // While the popup is open and messages are loaded, the count is derived live from receipts;
    // otherwise it comes from the conversation summary (prop or live broadcast).
    const summaryUnreadCount = isInternalAuth && !isProviderCPSupport
        ? (liveSummary?.clientPlacementTeamUnreadCount ?? conversationSummary?.clientPlacementTeamUnreadCount ?? 0)
        : (liveSummary?.providerUnreadCount ?? conversationSummary?.providerUnreadCount ?? 0);
    const messagesLoaded = isOpen && !isMessagesLoading;
    const sideLastReadAt = useMemo(() => getSideLastReadAt(readReceipts, isInternalAuth, currentUser?.guid), [readReceipts, isInternalAuth, currentUser?.guid]);
    // Read receipts are only observed via live broadcasts, so the initial cursor comes from the
    // conversation query (mySideLastReadAt): null = side never read, undefined = unknown (older
    // backend or not loaded yet) → fall back to the summary count rather than assuming epoch.
    const lastReadAt = useMemo(() => {
        if (!messagesLoaded) return sideLastReadAt;
        const readTimes = [sideLastReadAt, initialLastReadAt].filter((date): date is Date => date != null);
        if (readTimes.length > 0) return new Date(Math.max(...readTimes.map((date) => date.getTime())));
        return initialLastReadAt === undefined && sideLastReadAt === undefined ? undefined : null;
    }, [messagesLoaded, sideLastReadAt, initialLastReadAt]);
    const unreadCount = messagesLoaded && lastReadAt !== undefined ? countUnreadMessages(messages, lastReadAt, isInternalAuth) : summaryUnreadCount;
    const unreadDividerIndex = useMemo(() => findUnreadDividerIndex(messages, lastReadAt, isInternalAuth), [messages, lastReadAt, isInternalAuth]);

    const state = useMemo(() => ({urgent, hasConversation, unreadCount}), [urgent, hasConversation, unreadCount]);
    useReportConversationState(state, onStateChange);

    const openPopup = () => setUncontrolledIsOpen(true);

    useEffect(() => {
        if (!isOpen) autoReadAttemptedRef.current = false;
    }, [isOpen]);

    useEffect(() => {
        setConversationGuid(null);
        setMessages([]);
        setPageInfo(null);
        setInitialLastReadAt(undefined);
        senderHydrationRequestedGuidsRef.current.clear();
        isLoadingOlderRef.current = false;
        shouldScrollToBottomRef.current = true;
        isNearBottomRef.current = true;
        autoReadAttemptedRef.current = false;
    }, [offerGuid, setMessages]);

    useEffect(() => {
        setHasConversation(Boolean(conversationSummary?.hasConversation));
        setUrgent(Boolean(conversationSummary?.urgent));
    }, [offerGuid, conversationSummary?.hasConversation, conversationSummary?.urgent]);

    useEffect(() => {
        if (!liveSummary) {
            return;
        }
        setHasConversation(liveSummary.hasConversation);
        setUrgent(liveSummary.urgent);
    }, [liveSummary]);

    useOfferConversationData(data, setConversationGuid, setUrgent, setMessages, setPageInfo, senderCacheRef, shouldScrollToBottomRef, setInitialLastReadAt);
    useConversationScroll(isOpen, isLoadingOlder, messages, messagesContainerRef, pendingScrollRestoreRef, shouldScrollToBottomRef, isNearBottomRef, wasOpenRef);
    useLatestUrgentFromMessages(messages, setUrgent);
    useHydrateMissingSenders(conversationGuid, currentUser?.guid, messages, senderCacheRef, senderHydrationRequestedGuidsRef, loadSubmitters, bumpSenderCache);

    useEffect(() => {
        if (wasMessagingConnectedRef.current && !isMessagingConnected && hasConversation) {
            console.error('Messaging service disconnected', {
                offerGuid,
                conversationGuid,
                hasConversation,
                isLoading: isMessagesLoading,
                isLoadingOlder,
                messageCount: messages.length,
                unreadCount,
                timestamp: new Date().toISOString(),
            });
        }
        wasMessagingConnectedRef.current = isMessagingConnected;
    }, [hasConversation, isLoadingOlder, isMessagesLoading, isMessagingConnected, messages.length, offerGuid, conversationGuid, unreadCount]);

    const canToggleRead = useMemo(() => hasConversation && !isReadOnly && messages.length > 0, [hasConversation, isReadOnly, messages.length]);
    const canMarkAsUnread = useMemo(() => {
        const lastMessage = messages.slice().reverse().find((message) => message.messageType === 'TEXT');
        return canToggleRead && unreadCount === 0 && !!lastMessage && lastMessage.internal !== isInternalAuth;
    }, [canToggleRead, isInternalAuth, messages, unreadCount]);

    const loadOlderMessages = () => {
        if (!pageInfo?.hasNextPage || !pageInfo.endCursor || isLoadingOlderRef.current || !conversationGuid) return;
        isLoadingOlderRef.current = true;
        setIsLoadingOlder(true);
        const container = messagesContainerRef.current;
        if (container) pendingScrollRestoreRef.current = {height: container.scrollHeight, top: container.scrollTop};
        client.query<OfferMessagesQueryResult>({query: OFFER_MESSAGES_QUERY, variables: {conversationGuid, first: MESSAGE_PAGE_SIZE, after: pageInfo.endCursor}, fetchPolicy: 'no-cache'}).then((result) => {
            const nextPage = result.data?.messagesByConversation;
            if (!nextPage) return;
            const olderMessages = messagesFromEdges(nextPage.edges);
            cacheSenders(olderMessages, senderCacheRef.current);
            setMessages((current) => mergeMessages(current as Message[], olderMessages));
            setPageInfo(nextPage.pageInfo);
        }).catch((error) => {
            pendingScrollRestoreRef.current = null;
            console.error('Failed to load older offer messages', error);
        }).finally(() => {
            isLoadingOlderRef.current = false;
            setIsLoadingOlder(false);
        });
    };

    const handleMessageScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
        const container = event.currentTarget;
        isNearBottomRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < TOP_LOAD_THRESHOLD;
        if (container.scrollTop < TOP_LOAD_THRESHOLD) loadOlderMessages();
    };

    const markMessagesAsRead = useCallback(async (upToMessage: Message, targetConversationGuid?: string) => {
        const guid = targetConversationGuid ?? conversationGuid;
        if (!guid || !currentUser) return;
        try {
            await markOfferMessagesAsRead({variables: {conversationGuid: guid, upToMessageGuid: upToMessage.guid}});
            if (!isMountedRef.current) return;
            setReadReceipts((prev) => [
                ...prev.filter((receipt) => receipt.userGuid !== currentUser.guid),
                {
                    conversationGuid: guid,
                    userGuid: currentUser.guid,
                    internal: isInternalAuth,
                    lastReadMessageGuid: upToMessage.guid,
                    lastReadAt: upToMessage.createdAt,
                },
            ]);
        } catch (error: unknown) {
            console.error('Failed to mark offer messages as read', error);
            showToast.error(getDomainGraphqlError(error) ?? 'Failed to mark messages as read');
        }
    }, [conversationGuid, currentUser, isInternalAuth, isMountedRef, markOfferMessagesAsRead, setReadReceipts]);

    // Opening the popup on an unread conversation marks it as read once, after messages load.
    // Incoming messages while the popup is open are never auto-marked read.
    // The anchor and conversation guid come from the query result, not component state, because
    // the state updates land on the next render while this effect fires as soon as loading ends.
    useEffect(() => {
        if (!isOpen || isMessagesLoading || autoReadAttemptedRef.current) return;
        autoReadAttemptedRef.current = true;
        if (unreadCount === 0) return;
        const initialMessages = messagesFromEdges(data?.messagesByReferralAndProvider.edges ?? []);
        const latestTextMessage = initialMessages.reduce<Message | null>(
            (latest, message) => message.messageType === 'TEXT' && (!latest || message.createdAt > latest.createdAt) ? message : latest,
            null
        );
        if (latestTextMessage) {
            void markMessagesAsRead(latestTextMessage, data?.conversationByReferralAndProvider?.guid);
        }
    }, [data, isMessagesLoading, isOpen, markMessagesAsRead, unreadCount]);

    const handleMarkAsRead = useCallback(async () => {
        const latestMessage = messages.slice().reverse().find((message) => message.messageType === 'TEXT');
        if (latestMessage) await markMessagesAsRead(latestMessage);
    }, [messages, markMessagesAsRead]);

    const handleMarkAsUnread = useCallback(async () => {
        if (!conversationGuid) return;
        try {
            await markOfferMessagesAsUnread({variables: {conversationGuid}});
            if (!isMountedRef.current) return;
            const cursorGuid = getLastReadMessageGuidAfterMarkAsUnread(messages, isInternalAuth);
            const cursor = messages.find((message) => message.guid === cursorGuid);
            const lastReadAt = cursor?.createdAt ?? new Date(0).toISOString();
            setReadReceipts((prev) => prev.map((receipt) => isCurrentSideReceipt(receipt, isInternalAuth, currentUser?.guid)
                ? {...receipt, lastReadMessageGuid: cursorGuid, lastReadAt}
                : receipt));
            // Sync the initial cursor as well: lastReadAt is derived from the max of
            // sideLastReadAt and initialLastReadAt, so a backward move must also
            // update the snapshot from the conversation query or the UI stays read.
            setInitialLastReadAt(new Date(lastReadAt));
        } catch (error: unknown) {
            console.error('Failed to mark offer messages as unread', error);
            showToast.error(getDomainGraphqlError(error) ?? 'Failed to mark messages as unread');
        }
    }, [conversationGuid, currentUser?.guid, isInternalAuth, isMountedRef, markOfferMessagesAsUnread, messages, setReadReceipts, setInitialLastReadAt]);

    const handleSend = async () => {
        if (isReadOnly || !input.trim()) return;
        if (!isReferralProviderReady) {
            showToast.error('Still loading conversation details, please try again in a moment');
            return;
        }
        const content = input.trim();
        setInput('');
        try {
            const result = await sendOfferMessage({variables: {referralGuid, providerGuid, activeOfferGuid: offerGuid, content}});
            if (!isMountedRef.current) return;
            const message = result.data?.sendMessageByReferralAndProvider as Message | undefined;
            if (!message) {
                setInput(content);
                return;
            }
            setHasConversation(true);
            setConversationGuid(message.conversationGuid);
            shouldScrollToBottomRef.current = true;
            setMessages((current) => appendMessage(current as Message[], message));
            await markMessagesAsRead(message, message.conversationGuid);
        } catch (error) {
            console.error('Failed to send offer message', error);
            if (isMountedRef.current) setInput(content);
        }
    };

    const handleToggleUrgent = async () => {
        if (isReadOnly) return;
        if (!isReferralProviderReady) {
            showToast.error('Still loading conversation details, please try again in a moment');
            return;
        }
        try {
            const result = await toggleUrgentFlag({variables: {referralGuid, providerGuid, activeOfferGuid: offerGuid, urgent: !urgent}});
            if (isMountedRef.current) setUrgent(result.data?.toggleUrgentFlagByReferralAndProvider.urgent ?? !urgent);
        } catch (error) {
            console.error('Failed to update urgent flag', error);
        }
    };

    const close = () => {
        setUncontrolledIsOpen(false);
        onClose?.();
    };

    return {
        isOpen,
        open: openPopup,
        close,
        input,
        setInput,
        urgent,
        hasConversation,
        unreadCount,
        messages,
        isMessagesLoading,
        isLoadingOlder,
        isSendingMessage,
        isMarkingMessages,
        isReferralProviderReady,
        offerData,
        offerLoading,
        currentUser,
        isInternalAuth,
        isReadOnly,
        isMessagingConnected,
        senderCache: senderCacheRef.current,
        messagesEndRef,
        messagesContainerRef,
        handleMessageScroll,
        handleSend,
        handleToggleUrgent,
        handleMarkAsRead,
        handleMarkAsUnread,
        canToggleRead,
        canMarkAsUnread,
        unreadDividerIndex,
    };
}
